import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export interface LeadFilters {
  status: string;
  source: string;
  assignee: string;
  campaign: string;
  city: string[];
  state: string[];
  q: string;
  from: string;
  to: string;
  // "" (any) | "due" | "scheduled" — follow-up state.
  followUp: string;
  // "" (newest) | "follow_up_asc" | "follow_up_desc" — list sort.
  sort: string;
  page: number;
}

const EMPTY: LeadFilters = {
  status: "",
  source: "",
  assignee: "",
  campaign: "",
  city: [],
  state: [],
  q: "",
  from: "",
  to: "",
  followUp: "",
  sort: "",
  page: 0,
};

const PARAM_KEYS = [
  "status",
  "source",
  "assignee",
  "campaign",
  "city",
  "state",
  "q",
  "from",
  "to",
  "followUp",
  "sort",
  "page",
] as const;
type ParamKey = (typeof PARAM_KEYS)[number];

function parsePage(raw: string | null): number {
  if (!raw) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export interface UseLeadFiltersResult {
  filters: LeadFilters;
  isFiltered: boolean;
  activeCount: number;
  setStatus: (v: string) => void;
  setSource: (v: string) => void;
  setAssignee: (v: string) => void;
  setCampaign: (v: string) => void;
  setCity: (v: string[]) => void;
  setState: (v: string[]) => void;
  setSearch: (v: string) => void;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
  setFollowUp: (v: string) => void;
  setSort: (v: string) => void;
  setPage: (v: number) => void;
  clear: () => void;
}

export function useLeadFilters(): UseLeadFiltersResult {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<LeadFilters>(
    () => ({
      status: params.get("status") ?? "",
      source: params.get("source") ?? "",
      assignee: params.get("assignee") ?? "",
      campaign: params.get("campaign") ?? "",
      city: params.getAll("city"),
      state: params.getAll("state"),
      q: params.get("q") ?? "",
      from: params.get("from") ?? "",
      to: params.get("to") ?? "",
      followUp: params.get("followUp") ?? "",
      sort: params.get("sort") ?? "",
      page: parsePage(params.get("page")),
    }),
    [params]
  );

  const activeCount =
    (filters.status ? 1 : 0) +
    (filters.source ? 1 : 0) +
    (filters.assignee ? 1 : 0) +
    (filters.campaign ? 1 : 0) +
    (filters.city.length ? 1 : 0) +
    (filters.state.length ? 1 : 0) +
    (filters.q ? 1 : 0) +
    (filters.from || filters.to ? 1 : 0) +
    (filters.followUp ? 1 : 0);

  const isFiltered = activeCount > 0;

  const writeParam = useCallback(
    (key: ParamKey, value: string, opts: { resetPage?: boolean } = {}) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          if (opts.resetPage) next.delete("page");
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  // Multi-value writer: stores each selection as a repeated param
  // (?city=A&city=B) so values that contain commas stay intact.
  const writeMultiParam = useCallback(
    (key: ParamKey, values: string[]) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(key);
          for (const v of values) {
            if (v) next.append(key, v);
          }
          next.delete("page");
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const setStatus = useCallback((v: string) => writeParam("status", v, { resetPage: true }), [writeParam]);
  const setSource = useCallback((v: string) => writeParam("source", v, { resetPage: true }), [writeParam]);
  const setAssignee = useCallback((v: string) => writeParam("assignee", v, { resetPage: true }), [writeParam]);
  const setCampaign = useCallback((v: string) => writeParam("campaign", v, { resetPage: true }), [writeParam]);
  const setCity = useCallback((v: string[]) => writeMultiParam("city", v), [writeMultiParam]);
  const setState = useCallback((v: string[]) => writeMultiParam("state", v), [writeMultiParam]);
  const setSearch = useCallback((v: string) => writeParam("q", v, { resetPage: true }), [writeParam]);
  const setFrom = useCallback((v: string) => writeParam("from", v, { resetPage: true }), [writeParam]);
  const setTo = useCallback((v: string) => writeParam("to", v, { resetPage: true }), [writeParam]);
  const setFollowUp = useCallback((v: string) => writeParam("followUp", v, { resetPage: true }), [writeParam]);
  const setSort = useCallback((v: string) => writeParam("sort", v, { resetPage: true }), [writeParam]);
  const setPage = useCallback(
    (v: number) => writeParam("page", v > 0 ? String(v) : ""),
    [writeParam]
  );

  const clear = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        PARAM_KEYS.forEach((k) => next.delete(k));
        return next;
      },
      { replace: true }
    );
  }, [setParams]);

  return {
    filters,
    isFiltered,
    activeCount,
    setStatus,
    setSource,
    setAssignee,
    setCampaign,
    setCity,
    setState,
    setSearch,
    setFrom,
    setTo,
    setFollowUp,
    setSort,
    setPage,
    clear,
  };
}

export const EMPTY_FILTERS = EMPTY;
